import { Box, Divider } from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import { Tabs } from "components/Tabs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSowContext } from "store/sow";
import {
  compareStringsIgnoreCaseAndRemoveSpecialChars,
  getBadgeFromStatus,
} from "utils/functions";
import {
  HeaderBox,
  StyledBox,
  StyledButton,
  BoldText,
  DetailItem,
  DetailsBox,
} from "./ActivityItemProfile.style";
import CheckDocuments from "./CheckDocumentsModal/CheckDocumentsModal";
import LogSystem from "./LogSystemModal";
import ValidationSteps from "./ValidationSteps/ValidationSteps";
import { ActionButtons } from "../ActionButtons/ActionButtons";
import GridItemTS from "components/UI/Grid/GridItemTS";
import Card from "components/UI/Card/Card";
import CardBody from "components/UI/Card/CardBody";
import { SkeletonActivityItemProfile } from "./SkeletonActivityItemProfile/SkeletonActivityItemProfile";
import { toast } from "react-toastify";
import {
  ActivityItemStatusEnum,
  ActivityItemValidationStatusEnum,
  FlowStepsStatusEnum,
} from "utils/@types";
import { ActivityItemWorker } from "services/input-core/work-items/@types";

const TAB_VALUES = {
  "Check Documents": 0,
  "Log System": 1,
  "Automated Checks": 2,
  "Flow Details": 2,
};

const TABS = Object.keys(TAB_VALUES);

interface ActivityItemsProps {
  refID?: string;
  profile: "WORKER" | "SOW";
  entityDate?: {
    start: string;
    end: string;
  };
  supplierCode: string;
}

export default function ActivityItemProfile({
  profile,
  entityDate,
  refID,
  supplierCode,
}: ActivityItemsProps) {
  const activityItemSelected = useSowContext((ctx) => ctx.activityItemSelected);
  const activityItem = useSowContext((ctx) => ctx.activityItemDetails);
  const activityFlow = useSowContext((ctx) => ctx.activityFlow);
  const getActivityDetails = useSowContext((ctx) => ctx.getActivityDetails);
  const clearDetails = useSowContext((ctx) => ctx.clearDetails);
  const [selectedTab, setSelectedTab] = useState(TAB_VALUES["Check Documents"]);
  const { t } = useTranslation("modals");
  const containerRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getActivityDetailsByCode = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getActivityDetails(activityItemSelected);
      return response;
    } catch (error) {
      toast.error(t("danger-sow-not-found", { ns: "list-messages" }));
      setIsLoading(false);
      handleBack();
    } finally {
      setIsLoading(false);
    }
  }, [getActivityDetails, activityItemSelected]);

  useEffect(() => {
    getActivityDetailsByCode();
  }, [getActivityDetailsByCode]);

  const tabToShow = () => {
    switch (selectedTab) {
      case TAB_VALUES["Check Documents"]:
        return <CheckDocuments />;
      case TAB_VALUES["Log System"]:
        return <LogSystem />;
      case TAB_VALUES["Automated Checks"]:
        return <></>;
      default:
        return <ValidationSteps steps={activityFlow?.validationSteps} />;
    }
  };

  const handleBack = useCallback(() => {
    clearDetails();
  }, [clearDetails]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  if (isLoading) {
    return (
      <GridItemTS
        key="beltrame-activity-item-table-grid"
        xs={12}
        data-cy="beltrame-activity-item-table"
        ref={containerRef}
      >
        <Card>
          <CardBody>
            <SkeletonActivityItemProfile />
          </CardBody>
        </Card>
      </GridItemTS>
    );
  }

  const isFirstStep = (activityItem: ActivityItemWorker) => {
    return (
      activityItem?.validationStep === undefined ||
      activityItem?.validationStep === null ||
      (activityItem?.validationStep === 0 &&
        activityItem?.validationStatus !==
          ActivityItemValidationStatusEnum.REJECTED)
    );
  };

  function handleFormatUverifyStatus() {
    return !activityItem?.approvedBySuperUser
      ? getBadgeFromStatus(
          (() => {
            if (
              compareStringsIgnoreCaseAndRemoveSpecialChars(
                activityItem?.validationStatus,
                FlowStepsStatusEnum.SUPPLIER_UPLOADING_DOCUMENTS,
              )
            ) {
              return ActivityItemStatusEnum.UPLOADING_DOCUMENTS_SUPPLIER;
            }

            if (isFirstStep(activityItem)) {
              const waitingForDownloadStep = activityFlow?.validationSteps?.find(
                (step: any) =>
                  compareStringsIgnoreCaseAndRemoveSpecialChars(
                    step?.name,
                    FlowStepsStatusEnum.CHANGE_ACTIVITY_STATUS,
                  ),
              );

              if (waitingForDownloadStep) {
                return (
                  t(waitingForDownloadStep?.executedByRole[0]) +
                  " " +
                  t("Uploading Documents")
                );
              }
            }

            if (activityItem?.validationStep) {
              const configForValidationStep = activityFlow?.validationSteps?.find(
                (step: any) => step?.step === activityItem?.validationStep,
              );

              if (
                compareStringsIgnoreCaseAndRemoveSpecialChars(
                  configForValidationStep?.name,
                  FlowStepsStatusEnum.CHANGE_ACTIVITY_STATUS,
                )
              ) {
                return (
                  t(configForValidationStep?.executedByRole[0]) +
                  " " +
                  t("Uploading Documents")
                );
              }
            }

            return (
              activityItem?.validationStatus ??
              ActivityItemStatusEnum.UPLOADING_DOCUMENTS
            );
          })(),
          (text: string) => t(text, { ns: "status" }),
          activityItem?.validationExpirationDate,
          undefined,
          activityItem?.manualValidationOwner,
        )
      : getBadgeFromStatus(
          activityItem?.validationStatus,
          (text: string) => t(text, { ns: "status" }),
          activityItem?.validationExpirationDate,
          activityItem?.approvedBySuperUser,
          activityItem?.manualValidationOwner,
        );
  }

  return (
    <GridItemTS
      key="beltrame-activity-item-table-grid"
      xs={12}
      data-cy="beltrame-activity-item-table"
      ref={containerRef}
    >
      <Card>
        <CardBody>
          <StyledButton onClick={handleBack}>
            <ArrowBack /> Back
          </StyledButton>

          <StyledBox>
            <Divider style={{ backgroundColor: "#808080" }} />
            <HeaderBox>
              <Box
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <strong style={{ fontSize: "1.2rem" }}>
                  {activityItem?.activityItemDescription}
                </strong>
              </Box>
              <ActionButtons
                profile={profile}
                refID={refID}
                entityDate={entityDate}
                supplierCode={supplierCode}
              />
            </HeaderBox>
            <DetailsBox>
              <DetailItem>
                <BoldText>FieldGlass Status</BoldText>
                <p>
                  {getBadgeFromStatus(
                    activityItem?.activityItemStatus,
                    (text: string) => t(text, { ns: "status" }),
                  )}
                </p>
              </DetailItem>
              <DetailItem>
                <BoldText>Uverify Status</BoldText>
                <p>{handleFormatUverifyStatus()}</p>
              </DetailItem>
              <DetailItem>
                <BoldText>Due Date</BoldText>
                <p>{activityItem?.activityItemDueDate}</p>
              </DetailItem>
              <DetailItem>
                <BoldText>Code</BoldText>
                <p>{activityItem?.activityItemCode}</p>
              </DetailItem>
              {activityItem?.reasonForRejection && (
                <DetailItem>
                  <BoldText>Reason for Rejection</BoldText>
                  <p>{activityItem?.reasonForRejection}</p>
                </DetailItem>
              )}
            </DetailsBox>

            <Tabs
              actual={selectedTab}
              items={TABS}
              onSelectNewTab={(index) => setSelectedTab(index)}
            />
            <Box
              style={{
                border: "2px solid #f3f3f3",
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              }}
            >
              {tabToShow()}
            </Box>
          </StyledBox>
        </CardBody>
      </Card>
    </GridItemTS>
  );
}
